import { useAuth } from "react-oidc-context";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box, Card, CardActionArea, CardContent, Typography, Chip, Stack, Avatar,
  Divider, Skeleton, Alert,
} from "@mui/material";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingFlatRoundedIcon from "@mui/icons-material/TrendingFlatRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { useTenant } from "../context/TenantContext";
import { useAccess } from "../context/AccessContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";
import { iconFor } from "../config/icons";
import { displayName } from "../lib/identity";

interface Kpi {
  key: string; label: string; value: string | number;
  delta_pct: number | null; format: string;
}
interface KpiResponse { tenant: string; kpis: Kpi[]; stale: boolean; error?: string; }

interface DashboardItem {
  id: string; label: string; icon: string; color: string; desc: string;
  locked_reason?: string;
}
interface DashboardsResponse {
  tenant: string; available: DashboardItem[]; locked: DashboardItem[];
  stale: boolean; error?: string;
}

interface NotifEvent {
  id: string; level: "success" | "warning" | "error" | "info";
  title: string; detail: string; at?: string;
  kind: string; ref_id?: string;
}
interface NotifResponse { tenant: string; events: NotifEvent[]; stale: boolean; error?: string; }

const NOTIF_META: Record<NotifEvent["level"], { Icon: any; color: "success" | "warning" | "error" | "info" }> = {
  success: { Icon: CheckCircleRoundedIcon, color: "success" },
  warning: { Icon: WarningAmberRoundedIcon, color: "warning" },
  error:   { Icon: ErrorRoundedIcon,        color: "error" },
  info:    { Icon: InfoRoundedIcon,         color: "info" },
};

function friendlyDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return v; }
}

export function Home() {
  const auth = useAuth();
  const { tenant, tenants, isInternal } = useTenant();
  const { access, can, loading: accessLoading } = useAccess();
  const api = useApi();
  const firstName = displayName(auth.user?.profile);

  const kpisQ = useQuery({
    queryKey: ["kpis", tenant],
    queryFn: () => api.call<KpiResponse>("/api/kpis"),
    enabled: !!tenant && can("kpis"), staleTime: 30_000,
  });
  const dashQ = useQuery({
    queryKey: ["dashboards-catalog", tenant],
    queryFn: () => api.call<DashboardsResponse>("/api/dashboards"),
    enabled: !!tenant && can("dashboards"), staleTime: 60_000,
  });
  const notifQ = useQuery({
    queryKey: ["notifications-home", tenant],
    queryFn: () => api.call<NotifResponse>("/api/notifications?limit=5"),
    enabled: !!tenant && can("notificacoes"), staleTime: 30_000,
  });

  const featured = (dashQ.data?.available ?? []).slice(0, 3);

  return (
    <>
      <Card sx={{
        overflow: "hidden", position: "relative",
        background:
          "radial-gradient(600px 300px at 90% -20%, rgba(252,171,16,0.35) 0%, transparent 60%)," +
          "linear-gradient(135deg, #021a2c 0%, #2c4055 100%)",
        color: "common.white", border: "none",
      }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="overline" sx={{ color: "secondary.light" }}>
            Bem-vindo de volta
          </Typography>
          <Typography variant="h3" sx={{ color: "common.white", mt: 0.5 }}>
            Olá, {firstName} 👋
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, color: "rgba(255,255,255,0.85)", flexWrap: "wrap" }}>
            <Typography variant="body2">Tenant ativo:</Typography>
            <Chip label={tenant ?? "escolha um"} size="small" sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "common.white", fontWeight: 600 }} />
            {isInternal && (
              <Chip icon={<ShieldRoundedIcon sx={{ fontSize: 14, color: "#021a2c !important" }} />}
                label="admin interno" size="small" color="secondary" sx={{ fontWeight: 600 }} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {!tenant && tenants.length === 0 && !isInternal && (
        <Alert severity="warning" sx={{ borderRadius: 2 }} data-testid="sem-tenant">
          Seu usuário ainda não está vinculado a nenhuma empresa. Fale com o administrador.
        </Alert>
      )}

      {tenant && !accessLoading && access && !access.has_access && (
        <Alert severity="info" sx={{ borderRadius: 2 }} data-testid="sem-perfil">
          Você entrou em <strong>{tenant}</strong>, mas ainda não tem um perfil de acesso a dados nesta empresa.
          Fale com o administrador.
        </Alert>
      )}

      {can("kpis") && kpisQ.data?.stale && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          KPIs em cache ou fora do ar ({kpisQ.data.error ?? "unknown"}).
        </Alert>
      )}

      {can("kpis") && (
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        gap: 2,
      }}>
        {kpisQ.isLoading && Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} variant="outlined" sx={{ p: 2.5 }}>
            <Skeleton width={100} height={12} />
            <Skeleton width={140} height={36} sx={{ mt: 0.5 }} />
            <Skeleton width={60} height={12} />
          </Card>
        ))}
        {!kpisQ.isLoading && !kpisQ.data?.kpis?.length && (
          <Card variant="outlined" sx={{ p: 2.5, gridColumn: "1 / -1" }}>
            <Typography variant="body2" color="text.secondary">
              Ainda sem KPIs disponíveis para <strong>{tenant}</strong>.
            </Typography>
          </Card>
        )}
        {kpisQ.data?.kpis?.map((k) => {
          const delta = k.delta_pct;
          const up = delta != null && delta > 0.1;
          const down = delta != null && delta < -0.1;
          const TrendIcon = up ? TrendingUpRoundedIcon : down ? TrendingDownRoundedIcon : TrendingFlatRoundedIcon;
          const color = up ? "success.main" : down ? "error.main" : "text.secondary";
          return (
            <Card key={k.key} variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
                {k.label}
              </Typography>
              <Typography variant="h4" sx={{ color: "primary.dark", mt: 0.5 }}>
                {typeof k.value === "number" ? k.value.toLocaleString("pt-BR") : k.value}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, color }}>
                <TrendIcon fontSize="small" />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {delta == null ? "—" : `${up ? "+" : ""}${delta.toFixed(1)}%`}
                </Typography>
              </Stack>
            </Card>
          );
        })}
      </Box>
      )}

      {can("dashboards") && (<>
      <PageHeader
        title="Meus dashboards"
        action={<Chip label="Ver todos →" component={RouterLink} to="/dashboards" clickable variant="outlined" />}
      />
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
        gap: 2,
      }}>
        {dashQ.isLoading && Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} variant="outlined" sx={{ p: 2 }}>
            <Skeleton variant="circular" width={44} height={44} />
            <Skeleton width="60%" sx={{ mt: 1 }} />
            <Skeleton width="80%" />
          </Card>
        ))}
        {!dashQ.isLoading && featured.length === 0 && (
          <Card variant="outlined" sx={{ p: 2, gridColumn: "1 / -1" }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum dashboard configurado para <strong>{tenant}</strong>.
            </Typography>
          </Card>
        )}
        {featured.map((d) => {
          const Icon = iconFor(d.icon);
          return (
            <Card key={d.id} sx={{ "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 24px rgba(2,26,44,0.10)" } }}>
              <CardActionArea component={RouterLink} to={`/dashboards/${d.id}`} data-testid={`dashboard-${d.id}`}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: d.color, width: 44, height: 44 }}>
                      <Icon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ color: "primary.dark" }}>{d.label}</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>{d.desc}</Typography>
                    </Box>
                    <ArrowForwardRoundedIcon sx={{ color: "primary.main" }} />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
      </>)}

      {can("notificacoes") && (<>
      <PageHeader title="Atividade recente" subtitle={<>Eventos do tenant <strong>{tenant ?? "?"}</strong></>} />
      {notifQ.data?.stale && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {notifQ.data.error === "no_dataset" ? "Este tenant ainda não tem dados de atividade." :
           `Atividade indisponível (${notifQ.data.error ?? "unknown"}).`}
        </Alert>
      )}
      <Card variant="outlined">
        <CardContent sx={{ py: 1 }}>
          {notifQ.isLoading && Array.from({ length: 3 }).map((_, i) => (
            <Stack key={i} direction="row" spacing={2} alignItems="center" sx={{ py: 1.5 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box sx={{ flex: 1 }}><Skeleton width="70%" /><Skeleton width="30%" /></Box>
            </Stack>
          ))}
          {!notifQ.isLoading && (notifQ.data?.events?.length ?? 0) === 0 && (
            <Box sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
              <Typography variant="body2">Sem atividade recente para este tenant.</Typography>
            </Box>
          )}
          {notifQ.data?.events?.slice(0, 5).map((e, i, arr) => {
            const M = NOTIF_META[e.level] ?? NOTIF_META.info;
            return (
              <Box key={e.id}>
                <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ py: 1.5 }}>
                  <Avatar sx={{ bgcolor: `${M.color}.light`, color: `${M.color}.main`, width: 32, height: 32 }}>
                    <M.Icon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">{e.title}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {e.detail} · <span style={{ fontFamily: "monospace" }}>{friendlyDate(e.at)}</span>
                    </Typography>
                  </Box>
                </Stack>
                {i < arr.length - 1 && <Divider />}
              </Box>
            );
          })}
        </CardContent>
      </Card>
      </>)}
    </>
  );
}
